import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  BO_ROI_LY_DO_LABEL,
  type RegistryScope,
  type ReportPageRow,
  type ReportSummary,
} from 'shared';
import { api } from '../api';
import { useAuth } from '../auth';
import { Note } from '../components/Note';
import { StatStrip } from '../components/StatStrip';
import { DISPLAY, MONO, scoreHex, tokens } from '../theme';

/**
 * Màn Báo cáo — spec SC-08 §3.
 *
 * Đây là màn cấp QUẢN LÝ: nhìn cả danh sách chứ không nhìn một trang. Bước "Báo
 * cáo" trong thanh bảy bước đã đánh dấu `available: true` từ đầu nhưng chưa hề có
 * màn nào đứng sau nó — đây là màn đó.
 *
 * Nguyên tắc quan trọng nhất khi dựng: **phần chưa có dữ liệu ẨN HẲN**, không
 * hiện khối rỗng hay chữ "đang phát triển" (§3, tiêu chí nghiệm thu số 2). Người
 * xem báo cáo thường là quản lý, và một khối trống làm cả bản báo cáo trông dở
 * dang. Đó là lý do "Từ khoá" (spec SC-06) và "Trạng thái Google" (spec SC-05)
 * không xuất hiện ở đây dưới bất kỳ hình thức nào — kể cả một dòng hứa hẹn.
 */

function Diem({ v }: { v: number | null }) {
  if (v === null) return <Box component="span" sx={{ color: tokens.faint }}>—</Box>;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        minWidth: 30,
        px: 0.8,
        py: 0.1,
        borderRadius: '999px',
        bgcolor: scoreHex(v),
        color: '#fff',
        fontFamily: MONO,
        fontSize: 12.5,
        fontWeight: 700,
        textAlign: 'center',
      }}
    >
      {v}
    </Box>
  );
}

function Delta({ v }: { v: number | null }) {
  if (v === null || v === 0) {
    return <Box component="span" sx={{ color: tokens.faint, fontSize: 12.5 }}>—</Box>;
  }
  return (
    <Box
      component="span"
      sx={{ fontSize: 12.5, fontWeight: 600, color: v > 0 ? tokens.okText : tokens.roseText }}
    >
      {v > 0 ? `▲ ${v}` : `▼ ${Math.abs(v)}`}
    </Box>
  );
}

function BangTrang({ rows, coLyDo = false }: { rows: ReportPageRow[]; coLyDo?: boolean }) {
  if (rows.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: tokens.muted, py: 1.5 }}>
        Không có trang nào.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Trang</TableCell>
          {coLyDo && <TableCell>Vì sao</TableCell>}
          <TableCell align="right">Điểm</TableCell>
          <TableCell align="right">Thay đổi</TableCell>
          <TableCell align="right">Vấn đề</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.slug} hover>
            <TableCell>
              <Box
                component={RouterLink}
                to={`/pages/${r.slug}`}
                sx={{ color: tokens.ink, fontWeight: 600, textDecoration: 'none' }}
              >
                {r.code}
              </Box>
              {r.ownerName && (
                <Typography sx={{ fontSize: 12, color: tokens.faint }}>{r.ownerName}</Typography>
              )}
            </TableCell>
            {coLyDo && (
              <TableCell sx={{ fontSize: 13, color: tokens.muted }}>
                {r.lyDo ? BO_ROI_LY_DO_LABEL[r.lyDo] : '—'}
              </TableCell>
            )}
            <TableCell align="right">
              <Diem v={r.seoScore} />
            </TableCell>
            <TableCell align="right">
              <Delta v={r.delta} />
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: MONO, fontSize: 13 }}>
              {r.problemCount ?? '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Khoi({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px' }}>
      <Typography sx={{ fontSize: 16, fontWeight: 600, mb: hint ? 0.3 : 1.5 }}>{title}</Typography>
      {hint && <Typography sx={{ fontSize: 13, color: tokens.muted, mb: 1.5 }}>{hint}</Typography>}
      {children}
    </Paper>
  );
}

/** Ô đặt ngưỡng sinh cảnh báo — spec SC-08 §5, chỉ Web Administrator thấy. */
function NguongCanhBao() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['alerts', 'nguong'], queryFn: () => api.alertNguong() });
  const [nhap, setNhap] = useState<string>('');

  const luu = useMutation({
    mutationFn: (v: number) => api.datAlertNguong({ nguongTutDiem: v }),
    onSuccess: () => {
      setNhap('');
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const hienTai = data?.nguongTutDiem;
  // Ô nhập để TRỐNG cho tới khi người dùng gõ, rồi mới lấy giá trị của họ. Đổ sẵn
  // giá trị hiện tại vào state là mỗi lần server trả về số mới lại đè lên thứ
  // người dùng đang gõ dở.
  const giaTri = nhap === '' ? (hienTai ?? '') : nhap;
  const so = Number(giaTri);
  const hopLe = Number.isInteger(so) && so >= 1 && so <= 100;

  return (
    <Khoi
      title="Ngưỡng sinh cảnh báo"
      hint="Điểm SEO của một trang tụt từ mức này trở lên so với lần quét ngay trước thì người phụ trách nhận cảnh báo ở chuông trên thanh trên cùng."
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <TextField
          size="small"
          type="number"
          label="Số điểm"
          value={giaTri}
          onChange={(e) => setNhap(e.target.value)}
          error={giaTri !== '' && !hopLe}
          helperText={giaTri !== '' && !hopLe ? 'Nhập số nguyên từ 1 đến 100' : ' '}
          sx={{ width: 130 }}
        />
        <Button
          variant="outlined"
          disabled={!hopLe || so === hienTai || luu.isPending}
          onClick={() => luu.mutate(so)}
          sx={{ mt: 0.3 }}
        >
          {luu.isPending ? 'Đang lưu…' : 'Lưu'}
        </Button>
      </Stack>
      {luu.error && <Alert severity="error">{(luu.error as Error).message}</Alert>}
      <Typography sx={{ fontSize: 12.5, color: tokens.faint }}>
        Giá trị này dùng chung cho cả hệ thống, và mỗi lần đổi đều được ghi vào nhật ký kiểm toán.
      </Typography>
    </Khoi>
  );
}

function NoiDung({ s }: { s: ReportSummary }) {
  const chenh =
    s.diemTrungBinh !== null && s.diemTrungBinhKyTruoc !== null
      ? s.diemTrungBinh - s.diemTrungBinhKyTruoc
      : null;

  return (
    <Stack spacing={2.5}>
      <StatStrip
        items={[
          { value: String(s.tongSoTrang), label: 'Trang theo dõi' },
          {
            value: s.diemTrungBinh === null ? '—' : String(s.diemTrungBinh),
            label: 'Điểm SEO trung bình',
            color: s.diemTrungBinh === null ? undefined : scoreHex(s.diemTrungBinh),
          },
          {
            value: chenh === null || chenh === 0 ? '—' : chenh > 0 ? `▲ ${chenh}` : `▼ ${Math.abs(chenh)}`,
            label: 'So với lần quét trước',
            color:
              chenh === null || chenh === 0
                ? undefined
                : chenh > 0
                  ? tokens.okText
                  : tokens.roseText,
          },
          {
            value: String(s.chuaQuet),
            label: 'Chưa quét lần nào',
            color: s.chuaQuet > 0 ? tokens.amberText : undefined,
          },
        ]}
      />

      {s.boQuaVìDocHut > 0 && (
        <Note tone="warn">
          <b>{s.boQuaVìDocHut} trang</b> bị loại khỏi điểm trung bình vì nghi đọc hụt nội dung
          (trang dựng bằng JavaScript). Điểm của chúng không đáng tin, và gộp vào thì con số trung
          bình cũng vậy.
        </Note>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2.5,
        }}
      >
        <Khoi title="Trang tốt nhất">
          <BangTrang rows={s.tot} />
        </Khoi>
        <Khoi title="Trang cần chú ý">
          <BangTrang rows={s.kem} />
        </Khoi>
      </Box>

      {/* Ba khối dưới đây ẨN HẲN khi không có gì để nói — không hiện bảng rỗng. */}
      {s.tutNhieuNhat.length > 0 && (
        <Khoi
          title="Tụt điểm nhiều nhất"
          hint="So với lần quét ngay trước của chính trang đó. Chỉ liệt kê trang thật sự đi xuống."
        >
          <BangTrang rows={s.tutNhieuNhat} />
        </Khoi>
      )}

      {s.boRoi.length > 0 && (
        <Khoi
          title="Trang đang bị bỏ rơi"
          hint="Lâu chưa quét, chưa quét lần nào, hoặc điểm thấp mà không nhích lên qua các lần quét."
        >
          <BangTrang rows={s.boRoi} coLyDo />
        </Khoi>
      )}

      {s.vanDeHayGap.length > 0 && (
        <Khoi
          title="Vấn đề hay gặp nhất"
          hint="Sắp theo số trang dính, để biết sửa cái gì trước thì được nhiều trang cùng lúc."
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mục kiểm tra</TableCell>
                <TableCell align="right">Số trang</TableCell>
                <TableCell align="right">Tỉ lệ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {s.vanDeHayGap.map((v) => (
                <TableRow key={v.code} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{v.label}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: MONO, fontSize: 13 }}>
                    {v.soTrang}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                      <Box
                        sx={{
                          width: 60,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: tokens.line2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ width: `${v.tiLe}%`, height: '100%', bgcolor: tokens.amber }} />
                      </Box>
                      <Box component="span" sx={{ fontFamily: MONO, fontSize: 13, minWidth: 34 }}>
                        {v.tiLe}%
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Khoi>
      )}
    </Stack>
  );
}

export function BaoCaoPage() {
  const { user } = useAuth();
  const laAdmin = user?.role === 'WEB_ADMINISTRATOR';
  const [scope, setScope] = useState<RegistryScope>('mine');

  const report = useQuery({
    queryKey: ['report', 'tong-hop', scope],
    queryFn: () => api.reportSummary(scope),
  });

  const xuat = useMutation({ mutationFn: () => api.xuatBaoCao(scope) });

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'flex-end' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 700 }}>
            Báo cáo
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: tokens.muted, mt: 0.3 }}>
            Cả danh sách trong một trang: điểm chung, trang nào đang tụt, và nên sửa gì trước.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} sx={{ flex: 'none' }}>
          {laAdmin && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scope}
              onChange={(_, v: RegistryScope | null) => v && setScope(v)}
            >
              <ToggleButton value="mine" sx={{ textTransform: 'none', fontSize: 13, px: 1.6 }}>
                Trang của tôi
              </ToggleButton>
              <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: 13, px: 1.6 }}>
                Tất cả
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button
            variant="outlined"
            startIcon={
              xuat.isPending ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
            }
            disabled={xuat.isPending || !report.data}
            onClick={() => xuat.mutate()}
          >
            Xuất báo cáo
          </Button>
        </Stack>
      </Stack>

      {/* File tải về là HTML in được, không phải PDF — nói rõ để người dùng không
          đi tìm file .pdf trong thư mục Downloads. */}
      <Note>
        Nút <b>Xuất báo cáo</b> tải về một file HTML mở bằng trình duyệt và in được. Muốn có PDF để
        gửi đi thì mở file rồi <b>Ctrl+P → Lưu thành PDF</b> — cách này giữ đúng dấu tiếng Việt, còn
        các bộ sinh PDF tự động thì không.
      </Note>

      {xuat.error && <Alert severity="error">{(xuat.error as Error).message}</Alert>}
      {report.error && <Alert severity="error">{(report.error as Error).message}</Alert>}
      {report.isLoading && <CircularProgress size={24} />}
      {report.data && <NoiDung s={report.data} />}

      {laAdmin && <NguongCanhBao />}
    </Stack>
  );
}
